import { Section, Tags, buildKeywordRegex, highlight } from "./shared";

export default function MeetingNotes({ n }) {
  // Meetings don't have "keywords"; derive from tags for inline highlighting.
  const regex = buildKeywordRegex(n.tags);

  return (
    <>
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">/ Meeting</p>
      <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tighter mt-1">
        {n.title || "Meeting Notes"}
      </h1>

      {n.objective && (
        <p className="mt-3 text-sm text-zinc-600">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#3B82F6] mr-2">Objective</span>
          {highlight(n.objective, regex)}
        </p>
      )}

      {n.one_line_summary && (
        <p className="lead-summary mt-3 text-base sm:text-lg leading-relaxed text-zinc-800 border-l-2 border-[#3B82F6] pl-4">
          {highlight(n.one_line_summary, regex)}
        </p>
      )}

      <Tags tags={n.tags} />

      {n.key_decisions?.length > 0 && (
        <Section label="Key Decisions">
          <ul className="space-y-1">
            {n.key_decisions.map((d, i) => (
              <li key={i} className="text-sm leading-relaxed border-l-2 border-black pl-3 py-0.5">
                {highlight(d, regex)}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {n.important_discussion_points?.length > 0 && (
        <Section label="Discussion Points">
          <ul className="space-y-1 text-sm">
            {n.important_discussion_points.map((p, i) => (
              <li key={i} className="flex gap-2"><span className="text-[#3B82F6]">•</span>{highlight(p, regex)}</li>
            ))}
          </ul>
        </Section>
      )}

      {n.goals?.length > 0 && (
        <Section label="Goals">
          <ul className="space-y-1 text-sm">
            {n.goals.map((g, i) => (
              <li key={i} className="flex gap-2"><span className="text-[#3B82F6]">→</span>{highlight(g, regex)}</li>
            ))}
          </ul>
        </Section>
      )}

      {n.action_items?.length > 0 && (
        <Section label="Action Items">
          <div className="overflow-x-auto border border-black text-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-black bg-black text-white">
                  <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">Task</th>
                  <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">Owner</th>
                  <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">Deadline</th>
                </tr>
              </thead>
              <tbody>
                {n.action_items.map((a, i) => (
                  <tr key={i} className="border-b border-black/10 last:border-b-0">
                    <td className="px-3 py-1.5 align-top">{highlight(a.task, regex)}</td>
                    <td className="px-3 py-1.5 align-top text-zinc-600">{a.owner || "—"}</td>
                    <td className="px-3 py-1.5 align-top text-zinc-600">{a.deadline || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {n.risks?.length > 0 && (
        <Section label="Risks / Issues">
          <div className="bg-[#FF3333]/5 border-l-4 border-[#FF3333] p-3">
            <ul className="space-y-1 text-sm">
              {n.risks.map((r, i) => <li key={i}>{highlight(r, regex)}</li>)}
            </ul>
          </div>
        </Section>
      )}

      {n.pending_questions?.length > 0 && (
        <Section label="Pending Questions">
          <ul className="space-y-1 text-sm">
            {n.pending_questions.map((q, i) => (
              <li key={i} className="flex gap-2"><span className="text-zinc-400">?</span>{highlight(q, regex)}</li>
            ))}
          </ul>
        </Section>
      )}

      {n.follow_ups?.length > 0 && (
        <Section label="Follow-Ups">
          <ul className="space-y-1 text-sm">
            {n.follow_ups.map((f, i) => (
              <li key={i} className="flex gap-2"><span className="text-[#3B82F6]">↳</span>{highlight(f, regex)}</li>
            ))}
          </ul>
        </Section>
      )}

      {n.important_numbers?.length > 0 && (
        <Section label="Important Numbers">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {n.important_numbers.map((num, i) => (
              <div key={i} className="border border-black/15 p-2.5 bg-white">
                <div className="font-mono text-base font-bold leading-tight">{num.value}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">{num.label}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}
